import dynamic from 'next/dynamic';

import { SkeletonBlock } from '../../../../components/ToolkitV2/Skeleton/Skeleton';
import { useFinanceSummary } from './FinanceSummary.hook';
import { Text } from '../../../../components/ToolkitV2/Text/Text';
import { LinkButton } from '../../../../components/Toolkit/Button/LinkButton';
import { TextWithHelpIcon } from '../Panels/shared/TextWithHelpIcon/TextWithHelpIcon';

import { Spacer } from '../../../../helpers/genericStyles';

import * as Styled from './FinanceSummary.styled';

import type {
  LoanResponseSchema,
  Provider,
} from '../../../../components/CarFinancePage/queries/schema/carFinance.schema';
import { CURRENCIES } from '../../../../types';
import { type HirePurchaseModalProps } from '../HirePurchaseModal/HirePurchaseModal';
import { typeSafeObjectKeys } from '@dist-marketplace-frontend/utils';

const DynamicHirePurchaseModal = dynamic<HirePurchaseModalProps>(
  import('../HirePurchaseModal/HirePurchaseModal').then(
    (mod) => mod.HirePurchaseModal,
  ),
  { ssr: false },
);

const priceDefault = '€0';
const percentageDefault = '0.00%';
const termDefault = '0 years (0 months)';

const priceMapper = (value: string | number) =>
  new Intl.NumberFormat('en', {
    style: 'currency',
    currency: CURRENCIES.EUR,
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(Number(value));
const percentMapper = (value: string | number) => `${value}%`;
const termMapper = (value: string | number) =>
  `${Number(value) / 12} years (${value} months)`;

type Summary = Omit<
  LoanResponseSchema,
  | 'interest'
  | 'disclaimer'
  | 'warningEarlyRepayment'
  | 'warningMissedRepayments'
>;

type SummaryKeyMap = Record<
  keyof Summary,
  {
    label: string;
    mapper: (value: string | number) => string;
    default: string;
  }
>;

const keyMap = {
  totalDeposit: {
    label: 'Your Deposit',
    mapper: priceMapper,
    default: priceDefault,
  },
  financeAmount: {
    label: 'Finance Amount',
    mapper: priceMapper,
    default: priceDefault,
  },
  apr: {
    label: 'Typical APR',
    mapper: percentMapper,
    default: percentageDefault,
  },
  costOfCredit: {
    label: 'Total Cost of Credit',
    mapper: priceMapper,
    default: priceDefault,
  },
  totalPayable: {
    label: 'Total Amount Payable',
    mapper: priceMapper,
    default: priceDefault,
  },
  monthlyInstallments: {
    label: 'Term',
    mapper: termMapper,
    default: termDefault,
  },
  pricePerMonth: {
    label: 'Monthly Repayments',
    mapper: priceMapper,
    default: priceDefault,
  },
} satisfies SummaryKeyMap;

type FinanceSummaryArgs = {
  price: number;
  dealerId: string;
  financeHouse: Provider;
};

export const FinanceSummary = (args: FinanceSummaryArgs) => {
  const { price, dealerId, financeHouse } = args;

  const {
    fullDetailsText,
    isLoading,
    loanData,
    isHirePurchaseModalOpen,
    setIsHirePurchaseModalOpen,
    isFullDetailsOpen,
    setIsFullDetailsOpen,
  } = useFinanceSummary({
    dealerId,
    financeHouse,
  });

  return (
    <>
      <Styled.Wrapper>
        <Text size="L18" color="GREY_DARKER" weight="bold">
          Finance Summary
        </Text>
        <Spacer marginBottom={['M16', 'M24']} />
        <Styled.List>
          <Styled.Item>
            <Text
              color="GREY_DARK"
              size="M14"
              responsiveSizes={{ medium: 'M16' }}
            >
              Finance Type
            </Text>
            <TextWithHelpIcon
              size="M14"
              color="GREY_DARKER"
              weight="bold"
              responsiveSizes={{ medium: 'M16' }}
              aria-label="Hire purchase modal button"
              onClickIcon={() => setIsHirePurchaseModalOpen(true)}
              text="Hire Purchase"
            />
          </Styled.Item>
          <Styled.Item data-testid="price">
            <Text
              color="GREY_DARK"
              size="M14"
              responsiveSizes={{ medium: 'M16' }}
            >
              Retail Price
            </Text>
            <Text
              size="M14"
              weight="bold"
              color="GREY_DARKER"
              responsiveSizes={{ medium: 'M16' }}
            >
              {priceMapper(price)}
            </Text>
          </Styled.Item>
          {typeSafeObjectKeys(keyMap).map((key) => {
            return (
              <Styled.Item key={key} data-testid={key}>
                <Text
                  color="GREY_DARK"
                  size="M14"
                  responsiveSizes={{ medium: 'M16' }}
                >
                  {keyMap[key].label}
                </Text>
                <Text
                  size="M14"
                  weight="bold"
                  color={
                    loanData && !!loanData[key] ? 'GREY_DARKER' : 'GREY_DARK'
                  }
                  responsiveSizes={{ medium: 'M16' }}
                >
                  {isLoading ? (
                    <SkeletonBlock as="span" w="52px" h="14px" />
                  ) : (
                    <>
                      {loanData
                        ? keyMap[key].mapper(loanData[key])
                        : keyMap[key].default}
                    </>
                  )}
                </Text>
              </Styled.Item>
            );
          })}
        </Styled.List>
        <Spacer marginBottom="S8" />
        <LinkButton
          onClick={() => setIsFullDetailsOpen((prev) => !prev)}
          disabled={!loanData}
        >
          {fullDetailsText}
        </LinkButton>
        <Spacer marginBottom={['S8', 'M16']} />
        {isFullDetailsOpen && loanData && (
          <Styled.Disclaimer
            size="S12"
            color="GREY_DARK"
            responsiveSizes={{ medium: 'M14' }}
          >
            <span>{loanData.disclaimer}</span>
            {loanData.warningEarlyRepayment && (
              <strong>{loanData.warningEarlyRepayment}</strong>
            )}
            {loanData.warningMissedRepayments && (
              <strong>{loanData.warningMissedRepayments}</strong>
            )}
          </Styled.Disclaimer>
        )}
      </Styled.Wrapper>
      <DynamicHirePurchaseModal
        isOpen={isHirePurchaseModalOpen}
        onRequestClose={() => setIsHirePurchaseModalOpen(false)}
      />
    </>
  );
};
